import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, Typography } from '@mui/material';
import { ReactNode } from 'react';

type UtilityCardProps = {
  title: string;
  description: string;
  icon: ReactNode;
  emphasized?: boolean;
  disabled?: boolean;
  showDisabledMessage?: boolean;
  onClick?: () => void;
};

export default function UtilityCard({ title, description, icon, emphasized = false, disabled = false, showDisabledMessage = true, onClick }: UtilityCardProps) {
  const isDisabled = disabled || !onClick;
  let cardBorderColor = 'divider';
  let cardBackgroundColor = 'background.paper';
  let iconBackgroundColor = 'action.hover';

  if (isDisabled) {
    cardBorderColor = 'action.disabledBackground';
    cardBackgroundColor = 'action.disabledBackground';
    iconBackgroundColor = 'action.disabled';
  }

  if (emphasized) {
    cardBorderColor = 'primary.main';
    cardBackgroundColor = 'action.selected';
    iconBackgroundColor = 'primary.light';
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      sx={{
        width: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: cardBorderColor,
        backgroundColor: cardBackgroundColor,
        appearance: 'none',
        px: 2,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.7 : 1
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: iconBackgroundColor,
          color: isDisabled ? 'text.disabled' : 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 1.5
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" color={isDisabled ? 'text.disabled' : 'text.primary'} sx={{ lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography variant="body2" color={isDisabled ? 'text.disabled' : 'text.secondary'} noWrap>
          {description}
        </Typography>
        {isDisabled && showDisabledMessage && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
            Coming soon
          </Typography>
        )}
      </Box>
      <ChevronRightRoundedIcon sx={{ color: isDisabled ? 'text.disabled' : 'text.secondary' }} />
    </Box>
  );
}
